import mustache from "mustache";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { MonitoreoPlagaEnfermedadItem } from "../../../../../generated/grpc/catalogos_monitoreo_pb";
import { fx_createMonitoreoRelacionFincaCultivoPlagaRequest } from "../../../../../modulos/configuracion/monitoreo/request/MonitoreoRelacionFincaCultivoPlagaRequest";
import { MonitoreoRelacionFincaCultivoPlagaResponse } from "../../../../../modulos/configuracion/monitoreo/response/MonitoreoRelacionFincaCultivoPlagaResponse";
import { monitoreoRelacionFincaCultivoPlagaDeleteGrpcService } from "../../../../../modulos/configuracion/monitoreo/services/MonitoreoRelacionFincaCultivoPlagaDeleteGrpcService";
import { monitoreoRelacionFincaCultivoPlagaSaveGrpcService } from "../../../../../modulos/configuracion/monitoreo/services/MonitoreoRelacionFincaCultivoPlagaSaveGrpcService";
import { CultivoResponse } from "../../../../../modulos/catalogos/aplicaciones/response/CultivoResponse";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import template from "./cultivoPlagasTemplate.html";

mustache.parse(template);

/** Contador incremental de instancias, para no colisionar ids en el DOM. */
let CULTIVO_PLAGAS_ID: number = 0;

/** Clases de color por grupo, sobre el nombre normalizado (minúsculas, sin
 *  tildes y en singular). Los seis son los que hoy manda el catálogo; ácaro,
 *  insecto y hongo conservan los colores del mockup y los otros tres se
 *  eligieron para que se distingan entre sí. El server manda `grupo` como texto
 *  libre, así que cualquier grupo nuevo cae en `PALETA_FALLBACK`. */
const COLOR_POR_GRUPO: Record<string, string> = {
  acaro: "mc-grupo--rojo",
  insecto: "mc-grupo--ambar",
  hongo: "mc-grupo--violeta",
  oomiceto: "mc-grupo--teal",
  bacteria: "mc-grupo--azul",
  otro: "mc-grupo--neutro",
};

/** Colores para grupos que el catálogo agregue y no estén en `COLOR_POR_GRUPO`:
 *  se reparten por orden de aparición para que al menos se distingan entre sí. */
const PALETA_FALLBACK: string[] = ["mc-grupo--azul", "mc-grupo--neutro"];

/** Normaliza un nombre de grupo para buscarlo en `COLOR_POR_GRUPO`: minúsculas,
 *  sin diacríticos y en singular. `NFD` separa la tilde de la letra y
 *  `\p{Diacritic}` la descarta; la `s` final se recorta para que "Ácaro" y
 *  "Ácaros" resuelvan igual (el catálogo hoy manda singular, pero no cuesta
 *  nada tolerar las dos formas). */
const fx_normalizar = (texto: string): string =>
  texto
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase()
    .replace(/s$/, "");

/** Grupo de plagas ya armado para el template. */
type GrupoPlagas = {
  /** Nombre tal cual lo manda el server. */
  nombre: string;
  /** Nombre normalizado, usado como clave en el DOM. */
  clave: string;
  /** Clase de color aplicada al bloque. */
  cssClass: string;
  /** Plagas del grupo, en el orden del catálogo. */
  plagas: MonitoreoPlagaEnfermedadItem[];
};

/** Card de un cultivo de la finca con las plagas que se le monitorean,
 *  agrupadas y coloreadas por grupo. Colapsable desde el header.
 *
 *  Cada plaga es una relación independiente: encenderla es un `POST save` y
 *  apagarla un `DELETE` — no hay estado intermedio ni botón guardar. La pill se
 *  deshabilita mientras la petición viaja, que además evita el doble toggle. */
export class CultivoPlagasRender implements IConnectRender {
  /** Id de instancia, para los ids del DOM. */
  private readonly _id: number;

  /** Grupos ya armados (se conservan para repintar contadores). */
  private readonly _grupos: GrupoPlagas[];

  /** Relaciones activas del cultivo, indexadas por id de plaga. */
  private readonly _relaciones: Map<
    string,
    MonitoreoRelacionFincaCultivoPlagaResponse
  >;

  /**
   * @param _fincaId   Finca a la que pertenece el cultivo.
   * @param _cultivo   Cultivo de la card.
   * @param _lotes     Nombres de los lotes que tienen este cultivo.
   * @param plagas     Plagas del catálogo que aplican a este cultivo.
   * @param relaciones Relaciones ya guardadas de este cultivo.
   * @param _abierto   Si la card arranca desplegada.
   */
  constructor(
    private readonly _fincaId: string,
    private readonly _cultivo: CultivoResponse,
    private readonly _lotes: string[],
    plagas: MonitoreoPlagaEnfermedadItem[],
    relaciones: MonitoreoRelacionFincaCultivoPlagaResponse[],
    private readonly _abierto: boolean,
  ) {
    this._id = ++CULTIVO_PLAGAS_ID;
    this._grupos = this._agruparPlagas(plagas);
    this._relaciones = new Map(
      relaciones.map((relacion) => [relacion.plagaEnfermedadId, relacion]),
    );
  }

  /** HTML de la card con los grupos y el estado actual de cada plaga. */
  render(): string {
    return mustache.render(template, {
      id: this._id,
      nombre: this._cultivo.nombre,
      lotes: this._lotes.join(" · "),
      seleccionadas: this._relaciones.size,
      total: this._totalPlagas(),
      abierto: this._abierto,
      grupos: this._grupos.map((grupo) => ({
        nombre: grupo.nombre,
        clave: grupo.clave,
        cssClass: grupo.cssClass,
        seleccionadas: this._seleccionadasDe(grupo),
        total: grupo.plagas.length,
        plagas: grupo.plagas.map((plaga) => ({
          id: plaga.id,
          nombreComun: plaga.nombreComun,
          nombreTecnico: plaga.nombreTecnico,
          activa: this._relaciones.has(plaga.id),
        })),
      })),
    });
  }

  /** Engancha el colapso del header y el toggle de las pills. */
  connect(): void {
    this._card = document.getElementById(`mcCultivo-${this._id}`);
    this._header = document.getElementById(`mcCultivoHeader-${this._id}`);
    this._count = document.getElementById(`mcCultivoCount-${this._id}`);

    this._header?.addEventListener("click", this._onHeaderClick);
    this._card?.addEventListener("click", this._onPillClick);
  }

  dispose(): void {
    this._disposed = true;
    this._header?.removeEventListener("click", this._onHeaderClick);
    this._card?.removeEventListener("click", this._onPillClick);
  }

  /** Elemento raíz de la card. */
  private _card: HTMLElement | null = null;
  /** Header clickeable (colapso). */
  private _header: HTMLElement | null = null;
  /** Badge con el total de plagas marcadas del cultivo. */
  private _count: HTMLElement | null = null;

  /** Marca de componente desmontado: nada debe tocar el DOM después. */
  private _disposed: boolean = false;

  /** Agrupa las plagas por `grupo` respetando el `orden` del catálogo: los
   *  grupos salen en el orden de su primera plaga, y dentro de cada grupo las
   *  plagas quedan como vinieron (el catálogo ya llega ordenado por `orden`). */
  private _agruparPlagas(
    plagas: MonitoreoPlagaEnfermedadItem[],
  ): GrupoPlagas[] {
    const porClave = new Map<string, GrupoPlagas>();

    for (const plaga of [...plagas].sort((a, b) => a.orden - b.orden)) {
      const clave = fx_normalizar(plaga.grupo);
      const existente = porClave.get(clave);
      if (existente) {
        existente.plagas.push(plaga);
        continue;
      }

      porClave.set(clave, {
        nombre: plaga.grupo,
        clave: clave,
        cssClass:
          COLOR_POR_GRUPO[clave] ??
          PALETA_FALLBACK[porClave.size % PALETA_FALLBACK.length],
        plagas: [plaga],
      });
    }

    return Array.from(porClave.values());
  }

  /** Cantidad total de plagas ofrecidas para el cultivo. */
  private _totalPlagas(): number {
    return this._grupos.reduce((total, grupo) => total + grupo.plagas.length, 0);
  }

  /** Cantidad de plagas marcadas dentro de un grupo. */
  private _seleccionadasDe(grupo: GrupoPlagas): number {
    return grupo.plagas.filter((plaga) => this._relaciones.has(plaga.id)).length;
  }

  /** Despliega/pliega la card. */
  private _onHeaderClick = (): void => {
    this._card?.classList.toggle("mc-cultivo--open");
  };

  /** Toggle de una plaga: alta o baja de su relación, con la pill deshabilitada
   *  mientras viaja la petición. */
  private _onPillClick = (e: MouseEvent): void => {
    const pill = (e.target as HTMLElement).closest<HTMLButtonElement>(
      "button[data-plaga-id]",
    );
    if (!pill) return;

    const plagaId = pill.dataset.plagaId;
    if (!plagaId) return;

    pill.disabled = true;
    this._toggleAsync(plagaId)
      .catch((error) => {
        serviceProvider
          .getErrorService()
          .addError("Error al guardar las plagas a monitorear", error);
      })
      .finally(() => {
        if (this._disposed) return;
        pill.disabled = false;
        this._pintar();
      });
  };

  /** Alta o baja de la relación de una plaga, según cómo esté hoy. */
  private async _toggleAsync(plagaId: string): Promise<void> {
    const existente = this._relaciones.get(plagaId);

    if (existente) {
      await monitoreoRelacionFincaCultivoPlagaDeleteGrpcService.deleteAsync(
        existente,
      );
      this._relaciones.delete(plagaId);
      return;
    }

    const request = fx_createMonitoreoRelacionFincaCultivoPlagaRequest(
      this._fincaId,
      this._cultivo.id,
      plagaId,
    );
    const creada =
      await monitoreoRelacionFincaCultivoPlagaSaveGrpcService.saveAsync(request);
    this._relaciones.set(plagaId, creada);
  }

  /** Refleja el estado de las relaciones en las pills y los contadores. */
  private _pintar(): void {
    if (this._disposed || !this._card) return;

    this._card
      .querySelectorAll<HTMLButtonElement>("button[data-plaga-id]")
      .forEach((pill) => {
        const activa = this._relaciones.has(pill.dataset.plagaId ?? "");
        pill.classList.toggle("mc-pill--active", activa);
      });

    this._grupos.forEach((grupo) => {
      const count = this._card?.querySelector(
        `.mc-grupo-count[data-grupo="${grupo.clave}"]`,
      );
      if (count) {
        count.textContent = `${this._seleccionadasDe(grupo)} de ${grupo.plagas.length}`;
      }
    });

    if (this._count) {
      this._count.textContent = `${this._relaciones.size} de ${this._totalPlagas()}`;
    }
  }
}
